// src/pages/Upload/ManualEntryPage.jsx
import React from "react";
import { useParams } from "react-router-dom";
import Hero from "../../../components/ui/Hero.jsx";
import Card from "../../../components/ui/Card.jsx";
import UploadTabs from "./UploadTabs.jsx";
import UploadBackButton from "./UploadBackButton.jsx";
import { CODE_LABELS, codeToLabel } from "./constants.js";
import ConfirmationModal from "../../../components/ui/ConfirmationModal.jsx";
import { createMortgageProduct, __productsInternals } from "../../../api/lenderPortal/products.js";

// (Optional) if your project has this store; we call it defensively to clear persisted badges.
let uploadStore;
try {
  // eslint-disable-next-line import/no-unresolved
  uploadStore = require("./uploadStore.js");
} catch {
  /* safe no-op if store not present */
}

/* ---------- small layout helpers ---------- */
function Section({ title, children }) {
  return (
    <div className="rounded-2xl border border-sky-200 bg-sky-50/40 p-5">
      <div className="text-lg font-semibold mb-3">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>
    </div>
  );
}
function Field({ label, required, error, children }) {
  return (
    <label className="block">
      <div className="mb-1 text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-600">*</span>}
      </div>
      {children}
      {error ? <p className="mt-1 text-xs text-rose-600">{error}</p> : null}
    </label>
  );
}

/* ---------- Complex BTL (mirrors the coloured columns of the BTL template) ---------- */
const COMPLEX_BTL_SECTIONS = [
  {
    key: "hmo",
    title: "HMO",
    numbers: [{ field: "hmo_max_rooms", label: "Max no. of rooms", min: 0, max: 100, integer: true }],
    flags: [],
  },
  {
    key: "mufb",
    title: "MUFB",
    subtitle: "Multi Unit F/H Block",
    numbers: [
      { field: "mufb_max_units", label: "Max no. of units", min: 0, max: 100, integer: true },
      { field: "mufb_units_retained_percent", label: "% of units retained", min: 0, max: 100 },
    ],
    flags: [
      { field: "mufb_houses", label: "Houses" },
      { field: "mufb_flats", label: "Flats" },
    ],
  },
  {
    key: "mpot",
    title: "MPOT",
    subtitle: "Multiple Properties on One Title",
    numbers: [{ field: "mpot_max_units", label: "Max no. of units", min: 0, max: 100, integer: true }],
    flags: [
      { field: "mpot_flat", label: "Flat" },
      { field: "mpot_house", label: "House" },
      { field: "mpot_hmo", label: "HMO" },
      { field: "mpot_mufb", label: "MUFB" },
      { field: "mpot_holiday_let", label: "Holiday Let" },
      { field: "mpot_short_term_let", label: "Short Term Let" },
      { field: "mpot_semi_commercial", label: "Semi Comm" },
    ],
  },
  {
    key: "holidayLets",
    title: "Holiday Lets",
    numbers: [{ field: "holiday_let_average_weeks", label: "Average weeks", min: 0, max: 52 }],
    flags: [],
  },
  {
    key: "shortTermLets",
    title: "Short Term Lets",
    numbers: [{ field: "short_term_let_average_weeks", label: "Average weeks", min: 0, max: 52 }],
    flags: [{ field: "short_term_let_standard_rental_income", label: "Standard rental income" }],
  },
  {
    key: "portfolio",
    title: "Portfolio",
    numbers: [{ field: "portfolio_max_units", label: "Max no. of units", min: 0, max: 100, integer: true }],
    flags: [
      { field: "portfolio_btl", label: "BTL" },
      { field: "portfolio_hmo", label: "HMO" },
      { field: "portfolio_mufb", label: "MUFB" },
      { field: "portfolio_holiday_let", label: "Holiday Let" },
      { field: "portfolio_short_term_let", label: "Short Term Let" },
      { field: "portfolio_semi_commercial", label: "Semi Comm" },
    ],
  },
];

const complexErrorKey = (field) => `cx_${field}`;

function validateComplexBtl(values = {}) {
  const e = {};
  COMPLEX_BTL_SECTIONS.forEach(({ numbers }) => {
    numbers.forEach(({ field, min, max, integer }) => {
      const raw = values[field];
      if (isEmpty(raw)) return;
      const n = toNum(raw);
      if (isNaN(n)) e[complexErrorKey(field)] = "Must be a number.";
      else if (n < min || n > max) e[complexErrorKey(field)] = `Must be between ${min} and ${max}.`;
      else if (integer && !Number.isInteger(n)) e[complexErrorKey(field)] = errWholeYears;
    });
  });
  return e;
}

/** Section keys with a positive number or a ticked option; any makes the product Complex BTL (CBTL). */
function selectedComplexSections(values = {}) {
  return COMPLEX_BTL_SECTIONS.filter(({ numbers, flags }) =>
    numbers.some(({ field }) => !isEmpty(values[field]) && toNum(values[field]) > 0) ||
    flags.some(({ field }) => !!values[field])
  ).map((s) => s.key);
}

function complexBtlPayload(values = {}) {
  const out = {};
  COMPLEX_BTL_SECTIONS.forEach(({ numbers, flags }) => {
    numbers.forEach(({ field }) => {
      out[field] = isEmpty(values[field]) ? 0 : toNum(values[field]);
    });
    flags.forEach(({ field }) => {
      out[field] = values[field] ? 1 : 0;
    });
  });
  return out;
}

function ComplexBtlPanel({ open, onToggle, values, onChange, errors }) {
  const selected = selectedComplexSections(values);
  const isComplex = selected.length > 0;
  return (
    <div className="rounded-2xl border border-sky-200 bg-sky-50/40 p-5">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls="complex-btl-panel"
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-lg font-semibold">Complex BTL</span>
          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
              isComplex
                ? "bg-sky-600 text-white border-sky-600"
                : "bg-white text-gray-600 border-sky-200"
            }`}
          >
            {isComplex ? "CBTL" : "Optional"}
          </span>
          <span className="hidden sm:inline text-sm text-gray-500 truncate">
            {isComplex
              ? COMPLEX_BTL_SECTIONS.filter((s) => selected.includes(s.key)).map((s) => s.title).join(" · ")
              : "Leave empty for a standard BTL product"}
          </span>
        </div>
        <svg
          className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div id="complex-btl-panel" className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
          {COMPLEX_BTL_SECTIONS.map((section) => (
            <div key={section.key}>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-sm font-semibold text-gray-800">{section.title}</span>
                {section.subtitle ? <span className="text-xs text-gray-500">{section.subtitle}</span> : null}
              </div>
              <div className="space-y-3">
                {section.numbers.map(({ field, label, min, max, integer }) => {
                  const err = errors[complexErrorKey(field)];
                  return (
                    <Field key={field} label={label} error={err}>
                      <input
                        data-key={complexErrorKey(field)}
                        type="number"
                        min={min}
                        max={max}
                        step={integer ? "1" : "0.01"}
                        className={`w-full border rounded p-2 bg-white ${err ? "border-rose-500" : ""}`}
                        placeholder={`${min}-${max}`}
                        value={values[field] ?? ""}
                        onChange={(e) => onChange(field, e.target.value)}
                      />
                    </Field>
                  );
                })}
                {section.flags.length > 0 && (
                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                    {section.flags.map(({ field, label }) => (
                      <label key={field} className="inline-flex items-center gap-2 text-slate-800">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                          checked={!!values[field]}
                          onChange={(e) => onChange(field, e.target.checked)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* --------- file validation helpers (spreadsheets only) ---------- */
const ALLOWED_MIME = new Set([
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.spreadsheet",
]);
const ALLOWED_EXT = /\.(csv|xls|xlsx|ods)$/i;
const MAX_MB = 10;

function validateSpreadsheet(file) {
  if (!file) return "This document is required.";
  const typeOk =
    ALLOWED_EXT.test(file.name) || ALLOWED_MIME.has(file.type || "text/csv");
  if (!typeOk) return "Please upload a spreadsheet (.csv, .xls, .xlsx, .ods).";
  if (file.size > MAX_MB * 1024 * 1024)
    return `File is too large (> ${MAX_MB}MB).`;
  return null;
}

/* ---------- simple validators ---------- */
const isEmpty = (v) => String(v ?? "").trim() === "";
const isInt = (v) => String(Number(v)) === String(parseInt(v, 10));
const toNum = (v) => Number(v);

const errRequired = "This field is required.";
const errNonNeg = "Value cannot be negative.";
const errWholeYears = "Must be a whole number.";
const errYearsRange = "Must be between 0 and 40.";
const errAtLeast100 = "Must be ≥ 100%.";
const errGreaterThanZero = "Must be > 0.";

/* ---------- helpers ---------- */
function normalizeYesNo(v) {
  if (v === "Yes" || v === "No" || v === "" || v == null) return v ?? "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  const s = String(v).trim().toLowerCase();
  if (s === "true" || s === "1") return "Yes";
  if (s === "false" || s === "0") return "No";
  return v;
}
const to01 = (b) => (b ? 1 : 0);

function parseMulti(value) {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function hasMulti(value, option) {
  return parseMulti(value).includes(option);
}

function toggleMulti(value, option) {
  const current = parseMulti(value);
  const idx = current.indexOf(option);
  if (idx === -1) {
    current.push(option);
  } else {
    current.splice(idx, 1);
  }
  return current.join(", ");
}

/**
 * Format file size in KB or MB
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size (e.g., "1.5 MB" or "500 KB")
 */
function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return "0 KB";
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(2)} KB`;
  }
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
}

/**
 * Convert HTML datetime-local value ("YYYY-MM-DDTHH:MM")
 * to Frappe-friendly "YYYY-MM-DD HH:MM:SS"
 */
function toFrappeDateTime(dtLocal) {
  if (!dtLocal) return "";
  const [date, timeRaw] = String(dtLocal).split("T");
  if (!date) return "";
  const time =
    (timeRaw || "00:00").length === 5 ? `${timeRaw}:00` : timeRaw || "00:00:00";
  return `${date} ${time}`;
}

/* ---------- Ultra-sleek selected chip (only "X" to remove) ---------- */
function FileChip({ file, onClear }) {
  if (!file) return null;
  const fileSize = formatFileSize(file.size);

  // Get file extension
  const ext = file.name.split(".").pop()?.toLowerCase() || "";

  // Icon color based on file type
  const getIconColor = () => {
    if (["xlsx", "xls"].includes(ext)) return "text-emerald-600";
    if (ext === "csv") return "text-blue-600";
    if (ext === "ods") return "text-orange-600";
    return "text-slate-600";
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* File icon */}
          <div className="flex-shrink-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200">
              <svg
                className={`w-5 h-5 ${getIconColor()}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>

          {/* File info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="truncate font-medium text-slate-900 text-sm max-w-[280px]">
                {file.name}
              </div>
              <div className="flex-shrink-0 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                {fileSize}
              </div>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {ext.toUpperCase()} file • Ready to upload
            </div>
          </div>
        </div>

        {/* Only X (remove) */}
        <div className="flex items-center gap-2 ml-3">
          <button
            type="button"
            title="Remove file"
            aria-label="Remove file"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 bg-white hover:bg-rose-50 hover:border-rose-300 hover:text-rose-600 text-slate-500 transition-colors focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-1"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function ManualEntryBtlForm() {
  const { ptype } = useParams();
  const productLabel =
    (typeof codeToLabel === "function" ? codeToLabel(ptype) : null) ||
    CODE_LABELS[ptype];

  /* -------------------- form state -------------------- */
  const [form, setForm] = React.useState({
    /* Basic info */
    productName: "",
    productType: "",
    lenderCode: "",
    expiryDate: "",
    purchaseType: "",
    mortgageTerm: "",
    productTerm: "",
    borrowerType: "",
    epcGreen: "",
    /* Mortgage numbers */
    ltv: "",
    minLoan: "",
    maxLoan: "",
    rate: "",
    aprc: "",
    svr: "",
    /* Coverage */
    dscBasic: "",
    dscHigh: "",
    icr: "",
    /* Fees */
    prodFee: "",
    productFeeMode: "pound", // "pound" | "percent" | "min"
    appFee: "",
    /* ERCs */
    erc1: "",
    erc2: "",
    erc3: "",
    erc4: "",
    erc5: "",
    /* Property requirements */
    homeOwner: "",
    btlExp: "",
    creditWorth: "",
    tenancyType: "",
    leaseYears: "",
    tenure: "",
    exLocal: "",
    depositOrig: "",
    day1Remo: "",
    /* Features */
    cashback: "",
    features: {
      Portable: false,
      Overpayments: false,
      Underpayments: false,
      "Payment Holiday": false,
      "Free Legals": false,
      "Free Valuation": false,
    },
    /* Complex BTL (optional) */
    complex: {},
    /* Scheduling */
    scheduleEnabled: false,
    scheduleAt: "",
    /* Docs */
    valuationDoc: null,
    legalFeesDoc: null,
  });
  const [complexOpen, setComplexOpen] = React.useState(false);

  /* submit state */
  const [submitting, setSubmitting] = React.useState(false);
  // eslint-disable-next-line no-unused-vars
  const [submitError, setSubmitError] = React.useState("");
  // eslint-disable-next-line no-unused-vars
  const [submitOk, setSubmitOk] = React.useState("");
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [uploadResult, setUploadResult] = React.useState(null);

  /* errors shown in the UI */
  const [errors, setErrors] = React.useState({});

  /* upload progress tracking */
  const [uploadingFiles, setUploadingFiles] = React.useState({}); // { valuationDoc: { progress, name }, legalFeesDoc: { progress, name } }
  const [uploadedFileUrls, setUploadedFileUrls] = React.useState({}); // { valuationDoc: fileUrl, legalFeesDoc: fileUrl }
  
  /* ---------- Immediate file upload handler ---------- */
  const handleImmediateFileUpload = async (file, fieldKey) => {
    if (!file) return;
    
    // Validate first
    const validationError = fieldKey === "valuationDoc" 
      ? validateSpreadsheet(file)
      : validateSpreadsheet(file);
    
    if (validationError) {
      setErrors((prev) => ({
        ...prev,
        [fieldKey]: validationError,
      }));
      return;
    }
    
    // Set file in form
    setField(fieldKey, file);
    setErrors((prev) => ({
      ...prev,
      [fieldKey]: null,
    }));
    
    // Start upload immediately
    setUploadingFiles((prev) => ({
      ...prev,
      [fieldKey]: { progress: 0, name: file.name },
    }));
    
    try {
      const uploadFileGetUrl = __productsInternals.uploadFileGetUrl;
      const fileUrl = await uploadFileGetUrl(
        file,
        null,
        (progress) => {
          setUploadingFiles((prev) => ({
            ...prev,
            [fieldKey]: { progress, name: file.name },
          }));
        }
      );
      
      // Store uploaded URL
      setUploadedFileUrls((prev) => ({
        ...prev,
        [fieldKey]: fileUrl,
      }));
      
      // Clear progress after a brief delay
      setTimeout(() => {
        setUploadingFiles((prev) => {
          const next = { ...prev };
          delete next[fieldKey];
          return next;
        });
      }, 500);
    } catch (error) {
      console.error(`❌ Failed to upload ${fieldKey}:`, error);
      setErrors((prev) => ({
        ...prev,
        [fieldKey]: error?.message || "Failed to upload file. Please try again.",
      }));
      setUploadingFiles((prev) => {
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
      // Clear file from form on error
      setField(fieldKey, null);
    }
  };

  /* ---------- HARD purge of any persisted "old top files" on mount ---------- */
  React.useEffect(() => {
    try {
      // 1) Clear any obvious localStorage keys used by upload tabs/badges
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i) || "";
        if (
          /(upload|docs|valuation|legal|uploaddocs|upload-tabs|dynesourcer-upload)/i.test(
            key
          )
        ) {
          toRemove.push(key);
        }
      }
      toRemove.forEach((k) => localStorage.removeItem(k));

      // 2) If a Zustand uploadStore exists with setDocs/reset, nuke it
      if (uploadStore?.useUploadStore?.getState) {
        const st = uploadStore.useUploadStore.getState();
        st?.setDocs?.({ valuationDoc: null, legalFeesDoc: null });
        st?.reset?.();
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const setField = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      // if switching away from LEASEHOLD entirely, clear leaseYears + error
      if (k === "tenure" && !hasMulti(v, "LEASEHOLD")) {
        next.leaseYears = "";
        setErrors((prev) => {
          const copy = { ...prev };
          delete copy.leaseYears;
          return copy;
        });
      }
      return next;
    });

    // live-validate
    setErrors((prev) => ({
      ...prev,
      [k]: validateField(k, v, { ...form, [k]: v }) || null,
    }));

    // keep min/max relation
    if (k === "minLoan" || k === "maxLoan") {
      setErrors((prev) => {
        const next = { ...prev };
        const relError = validateMinMaxLoan({ ...form, [k]: v });
        next.maxLoan = relError;
        return next;
      });
    }

    if (k === "scheduleEnabled") {
      setErrors((prev) => ({
        ...prev,
        scheduleAt:
          validateField("scheduleAt", form.scheduleAt, {
            ...form,
            [k]: v,
          }) || null,
      }));
    }
  };

  const setComplexField = (field, val) => {
    const nextComplex = { ...form.complex, [field]: val };
    setForm((f) => ({ ...f, complex: { ...f.complex, [field]: val } }));
    const key = complexErrorKey(field);
    setErrors((prev) => ({ ...prev, [key]: validateComplexBtl(nextComplex)[key] || null }));
  };

  const setFeature = (name, val) => {
    setForm((f) => {
      const next = { ...f, features: { ...f.features, [name]: val } };
      if (name === "Free Valuation" && val) {
        next.valuationDoc = null; // clear local file
      }
      return next;
    });
    if (name === "Free Valuation") {
      setErrors((prev) => ({
        ...prev,
        valuationDoc: val ? null : prev.valuationDoc ?? null,
      }));
    }
  };

  /* ---------- min date for expiry (>= 24h / tomorrow local) ---------- */
  const minExpiryDate = React.useMemo(() => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  /* ---------- max date for expiry (20 years from now) ---------- */
  const maxExpiryDate = React.useMemo(() => {
    const now = new Date();
    const d = new Date(now.getFullYear() + 20, now.getMonth(), now.getDate());
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  /* helpful flags */
  const freeValuationOn = !!form.features["Free Valuation"];
  const isLeasehold = hasMulti(form.tenure, "LEASEHOLD");
  const hasValuation = !!form.valuationDoc;
  const hasLegal = !!form.legalFeesDoc;

  // Refs to trigger file picker
  const valuationInputRef = React.useRef(null);
  const legalInputRef = React.useRef(null);

  /* ---------- validation ---------- */
  function validateField(key, value, full) {
    const requiredKeys = new Set([
      "productName",
      "productType",
      "lenderCode",
      "expiryDate",
      "purchaseType",
      "mortgageTerm",
      "productTerm",
      "borrowerType",
      "epcGreen",
      "ltv",
      "minLoan",
      "maxLoan",
      "rate",
      "aprc",
      "svr",
      "dscBasic",
      "dscHigh",
      "icr",
      "prodFee",
      "appFee",
      "erc1",
      "erc2",
      "erc3",
      "erc4",
      "erc5",
      // property
      "homeOwner",
      "btlExp",
      "creditWorth",
      "tenancyType",
      "tenure",
      "exLocal",
      "depositOrig",
      "day1Remo",
    ]);

    if (hasMulti(full?.tenure, "LEASEHOLD")) requiredKeys.add("leaseYears");

    if (requiredKeys.has(key) && isEmpty(value)) return errRequired;

    if (key === "lenderCode") {
      const ok = /^[a-z0-9]+$/i.test(String(value || "").trim());
      if (!ok) return "Alphanumeric only (no spaces or symbols).";
      return null;
    }

    if (key === "expiryDate") {
      if (isEmpty(value)) return errRequired;
      
      // Validate date format (YYYY-MM-DD)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(value)) {
        return "Invalid date format. Please use YYYY-MM-DD format.";
      }
      
      const [yy, mm, dd] = String(value).split("-").map(Number);
      
      // Validate year range (reasonable range: 2000 to 2100)
      if (yy < 2000 || yy > 2100) {
        return "Year must be between 2000 and 2100.";
      }
      
      // Validate month range
      if (mm < 1 || mm > 12) {
        return "Month must be between 1 and 12.";
      }
      
      // Validate day range
      if (dd < 1 || dd > 31) {
        return "Day must be between 1 and 31.";
      }
      
      // Create date object and validate it's a real date
      const picked = new Date(yy, mm - 1, dd);
      
      // Check if the date is valid (handles cases like Feb 30, etc.)
      if (
        picked.getFullYear() !== yy ||
        picked.getMonth() !== mm - 1 ||
        picked.getDate() !== dd
      ) {
        return "Invalid date. Please check the day, month, and year.";
      }
      
      // Check if date is in the past
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const selectedDate = new Date(yy, mm - 1, dd);
      
      if (selectedDate < today) {
        return "Expiry date cannot be in the past.";
      }
      
      // Check if date is at least 24 hours from now
      const min = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      if (selectedDate < min) {
        const minStr = `${min.getFullYear()}-${String(min.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(min.getDate()).padStart(2, "0")}`;
        return `Must be at least 24 hours from now (≥ ${minStr}).`;
      }
      
      // Check if date is not too far in the future (max 20 years)
      const maxDate = new Date(now.getFullYear() + 20, now.getMonth(), now.getDate());
      if (selectedDate > maxDate) {
        return "Expiry date cannot be more than 20 years in the future.";
      }
      
      return null;
    }

    if (key === "mortgageTerm") {
      if (isEmpty(value)) return errRequired;
      if (!isInt(value)) return errWholeYears;
      const n = toNum(value);
      if (n < 0 || n > 40) return errYearsRange;
      return null;
    }

    if (key === "productTerm") {
      if (isEmpty(value)) return errRequired;
      return null;
    }

    if (key === "ltv") {
      if (isEmpty(value)) return errRequired;
      const n = toNum(value);
      if (isNaN(n)) return "Must be a number.";
      if (n < 0) return errNonNeg;
      if (n > 100) return "LTV cannot exceed 100%.";
      return null;
    }

    const nonNeg = new Set([
      "minLoan",
      "maxLoan",
      "rate",
      "aprc",
      "svr",
      "prodFee",
      "appFee",
      "erc1",
      "erc2",
      "erc3",
      "erc4",
      "erc5",
      "cashback",
      "leaseYears",
    ]);
    if (nonNeg.has(key)) {
      if (key === "leaseYears" && full?.tenure !== "LEASEHOLD") return null;
      if (isEmpty(value)) return errRequired;
      const n = toNum(value);
      if (isNaN(n)) return "Must be a number.";
      if (n < 0) return errNonNeg;
      return null;
    }

    // DSCs ≥ 100%
    const atLeast100 = new Set(["dscBasic", "dscHigh"]);
    if (atLeast100.has(key)) {
      if (isEmpty(value)) return errRequired;
      const n = toNum(value);
      if (isNaN(n)) return "Must be a number.";
      if (n < 100) return errAtLeast100;
      return null;
    }

    // ICR > 0
    if (key === "icr") {
      if (isEmpty(value)) return errRequired;
      const n = toNum(value);
      if (isNaN(n)) return "Must be a number.";
      if (n <= 0) return errGreaterThanZero;
      return null;
    }

    if (key === "scheduleAt") {
      if (full?.scheduleEnabled && isEmpty(value))
        return "Please choose a schedule date & time.";
      return null;
    }

    if (key === "valuationDoc") {
      if (full?.features?.["Free Valuation"]) return null;
      return validateSpreadsheet(value);
    }
    if (key === "legalFeesDoc") return validateSpreadsheet(value);

    return null;
  }

  function validateMinMaxLoan(full) {
    const min = full.minLoan;
    const max = full.maxLoan;
    if (isEmpty(max)) return errRequired;
    if (isNaN(toNum(max))) return "Must be a number.";
    if (!isEmpty(min) && !isNaN(toNum(min)) && toNum(max) < toNum(min)) {
      return "Max Loan must be ≥ Min Loan.";
    }
    return null;
  }

  function validateAll(current) {
    const keysToCheck = [
      "productName",
      "productType",
      "lenderCode",
      "expiryDate",
      "purchaseType",
      "mortgageTerm",
      "productTerm",
      "borrowerType",
      "epcGreen",
      "ltv",
      "minLoan",
      "maxLoan",
      "rate",
      "aprc",
      "svr",
      "dscBasic",
      "dscHigh",
      "icr",
      "prodFee",
      "appFee",
      "erc1",
      "erc2",
      "erc3",
      "erc4",
      "erc5",
      "cashback",
      // property
      "homeOwner",
      "btlExp",
      "creditWorth",
      "tenancyType",
      "tenure",
      "exLocal",
      "depositOrig",
      "day1Remo",
      // docs & schedule
      "valuationDoc",
      "legalFeesDoc",
      "scheduleAt",
    ];

    if (current.tenure === "LEASEHOLD") keysToCheck.push("leaseYears");

    const e = {};
    for (const k of keysToCheck) {
      const msg = validateField(k, current[k], current);
      if (msg) e[k] = msg;
    }
    const rel = validateMinMaxLoan(current);
    if (rel) e.maxLoan = rel;
    return { ...e, ...validateComplexBtl(current.complex) };
  }

  /* ---------- submit ---------- */
  const onSubmit = async (ev) => {
    ev.preventDefault();
    setSubmitError("");
    setSubmitOk("");

    const e = validateAll(form);
    setErrors(e);

    if (Object.keys(e).length > 0) {
      const firstKey = Object.keys(e)[0];
      const focusFirst = () => {
        const node = document.querySelector(`[data-key="${firstKey}"]`);
        node?.focus?.();
        node?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      };
      if (firstKey.startsWith("cx_") && !complexOpen) {
        setComplexOpen(true);
        setTimeout(focusFirst, 0);
      } else {
        focusFirst();
      }
      return;
    }

    const isComplexBtl = selectedComplexSections(form.complex).length > 0;

    const payload = {
      doctype: "Mortgage Product",

      // Basic info
      product_name: form.productName || "",
      product_expiray_date: form.expiryDate || "",
      product_termsyears: String(form.productTerm || ""),
      product_type: form.productType || "",
      loan_purpose: form.purchaseType || "",
      borrower_type: form.borrowerType || "",
      lender_product_code: form.lenderCode || "",
      mortgage_term_years: toNum(form.mortgageTerm) || 0,

      // Rates & bounds
      epc_rating_green: normalizeYesNo(form.epcGreen) || "No",
      ity_percent: toNum(form.ltv) || 0,
      product_rate_percent: toNum(form.rate) || 0,
      aprc_percent: toNum(form.aprc) || 0,
      svr_margin_percent: toNum(form.svr) || 0,
      min_loan: toNum(form.minLoan) || 0,
      max_loan: toNum(form.maxLoan) || 0,

      // Coverage
      basic_taxpayer_dsc_percent: toNum(form.dscBasic) || 0,
      high_taxpayer_dsc: toNum(form.dscHigh) || 0,
      icr_rate: toNum(form.icr) || 0,

      // Fees – map UI toggle to Frappe "Product Fee Type"
      product_fee_amount: toNum(form.prodFee) || 0,
      product_fee_type:
        form.productFeeMode === "percent"
          ? "Percentage"
          : form.productFeeMode === "min"
          ? "Mini Fee"
          : "Pound",
      product_fee_mode: form.productFeeMode || "pound",
      product_fee_percent:
        form.productFeeMode === "percent" ? toNum(form.prodFee) || 0 : 0,
      product_fee_minimum:
        form.productFeeMode === "min" ? toNum(form.prodFee) || 0 : 0,

      application_fee: toNum(form.appFee) || 0,

      // ERCs
      year_1: toNum(form.erc1) || 0,
      year_2: toNum(form.erc2) || 0,
      year_3: toNum(form.erc3) || 0,
      year_4: toNum(form.erc4) || 0,
      year_5: toNum(form.erc5) || 0,

      // Property requirements
      home_owner: form.homeOwner || "Any",
      tenancy_type: form.tenancyType || "AST",
      ex_local_authority: normalizeYesNo(form.exLocal) || "No",
      btl_experince_year: form.btlExp || "0",
      leasehold_remaining_termyears:
        form.tenure === "LEASEHOLD" ? toNum(form.leaseYears) || 0 : 999,
      deposit_originantion: form.depositOrig || "Savings",
      credit_worthiness: form.creditWorth || "Excellent 881+",
      fh_or_lh: form.tenure || "FREEHOLD",
      day_1_remortgage: form.day1Remo || "Market Value",

      // Features
      portable: to01(!!form.features?.Portable),
      free_legals: to01(!!form.features?.["Free Legals"]),
      overpayents: to01(!!form.features?.Overpayments),
      free_valuation: to01(!!form.features?.["Free Valuation"]),
      underpayments: to01(!!form.features?.Underpayments),
      payment_holiday: to01(!!form.features?.["Payment Holiday"]),
      cashback: toNum(form.cashback) || 0,

      // Complex BTL — only sent when filled, which makes the product CBTL
      ...(isComplexBtl ? { product_category: "CBTL", ...complexBtlPayload(form.complex) } : {}),

      // ⭐ Scheduling
      product_schedule: form.scheduleEnabled ? 1 : 0,
      date_time: form.scheduleEnabled ? toFrappeDateTime(form.scheduleAt) : null,

      // Files (handled in api layer)
      valuationDoc: form.valuationDoc,
      legalFeesDoc: form.legalFeesDoc,
    };

    try {
      setSubmitting(true);
      
      // Use pre-uploaded file URLs if available, otherwise upload now
      const finalPayload = { ...payload };
      
      // Remove file objects and use pre-uploaded URLs
      delete finalPayload.valuationDoc;
      delete finalPayload.legalFeesDoc;
      
      if (uploadedFileUrls.valuationDoc) {
        finalPayload.valuation_fee_spreadsheet = uploadedFileUrls.valuationDoc;
      }
      
      if (uploadedFileUrls.legalFeesDoc) {
        finalPayload.leagal_fees_spreadsheet = uploadedFileUrls.legalFeesDoc;
      }
      
      const res = await createMortgageProduct(finalPayload);
      const name = res?.data?.name || res?.message?.name || "Mortgage Product";
      
      // Set success result
      setUploadResult({
        type: "success",
        productName: form.productName || name,
        productId: name,
        isScheduled: form.scheduleEnabled && form.scheduleAt,
        scheduledAt: form.scheduleEnabled && form.scheduleAt 
          ? new Date(form.scheduleAt).toLocaleString() 
          : null,
        productType: form.productType,
        response: res,
      });
      
      setShowConfirmation(true);
      setSubmitOk(`Product created: ${name}`);
      
      // Clear selected files in UI after submit
      setForm((f) => ({
        ...f,
        valuationDoc: null,
        legalFeesDoc: null,
      }));
      
      // Clear upload progress
      setUploadingFiles({});
    } catch (err) {
      console.error("❌ Submit error:", err);
      const errorMsg = err?.message || err?.data?.message || "Failed to submit product";
      setSubmitError(errorMsg);
      
      // Set error result
      setUploadResult({
        type: "error",
        message: errorMsg,
        error: err,
      });
      setShowConfirmation(true);
      
      // Clear upload progress on error
      setUploadingFiles({});
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    if (uploadResult?.type === "success") {
      // Reset form after successful upload
      setSubmitOk("");
      setSubmitError("");
      setUploadResult(null);
    }
  };

  return (
    <>
      <Hero
        title={`Manual Entry — ${productLabel || "Select Product"}`}
        subtitle="Upload all products in one place"
      />
      <div className="mt-3">
        <UploadTabs ptype={ptype} />
      </div>

      <form className="space-y-6 mt-4" onSubmit={onSubmit} noValidate>
        {/* ---------- Basic Product Information ---------- */}
        <Section title="Basic Product Information">
          {/* Row 1 */}
          <Field label="Product Name" required error={errors.productName}>
            <input
              data-key="productName"
              className={`w-full border rounded p-2 bg-white ${
                errors.productName ? "border-rose-500" : ""
              }`}
              value={form.productName}
              onChange={(e) => setField("productName", e.target.value)}
              placeholder="e.g., 5-Year Fixed Mortgage"
            />
          </Field>

          <Field label="Product Type" required error={errors.productType}>
            <div className="flex gap-4 whitespace-nowrap text-sm">
              {["Fixed", "Variable", "Tracker", "Discounted"].map((opt) => {
                const active = hasMulti(form.productType, opt);
                return (
                  <label
                    key={opt}
                    className="inline-flex items-center gap-2 text-slate-800"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      checked={active}
                      onChange={() =>
                        setField("productType", toggleMulti(form.productType, opt))
                      }
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          </Field>

          <Field label="Lender Product Code">
            <input
              data-key="lenderCode"
              className={`w-full border rounded p-2 bg-white ${
                errors.lenderCode ? "border-rose-500" : ""
              }`}
              value={form.lenderCode}
              onChange={(e) => setField("lenderCode", e.target.value)}
              placeholder="e.g., ABC123"
            />
          </Field>

          {/* Row 2 */}
          <Field label="Product Expiry Date" required error={errors.expiryDate}>
            <input
              data-key="expiryDate"
              type="date"
              min={minExpiryDate}
              max={maxExpiryDate}
              className={`w-full border rounded p-2 bg-white ${
                errors.expiryDate ? "border-rose-500" : ""
              }`}
              value={form.expiryDate}
              onChange={(e) => {
                const inputValue = e.target.value;
                // Additional validation on change to prevent invalid dates
                if (inputValue) {
                  const [yy, mm, dd] = inputValue.split("-").map(Number);
                  // Basic sanity check before setting
                  if (yy >= 2000 && yy <= 2100 && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
                    setField("expiryDate", inputValue);
                  } else {
                    // If invalid, still set it but validation will catch it
                    setField("expiryDate", inputValue);
                  }
                } else {
                  setField("expiryDate", inputValue);
                }
              }}
              onBlur={(e) => {
                // Additional validation on blur
                const inputValue = e.target.value;
                if (inputValue) {
                  const [yy] = inputValue.split("-").map(Number);
                  if (yy < 2000 || yy > 2100) {
                    setErrors((prev) => ({
                      ...prev,
                      expiryDate: "Year must be between 2000 and 2100.",
                    }));
                  }
                }
              }}
            />
          </Field>

          <Field
            label="Mortgage Term (years)"
            required
            error={errors.mortgageTerm}
          >
            <input
              data-key="mortgageTerm"
              type="number"
              className={`w-full border rounded p-2 bg-white ${
                errors.mortgageTerm ? "border-rose-500" : ""
              }`}
              value={form.mortgageTerm}
              onChange={(e) => setField("mortgageTerm", e.target.value)}
              min={0}
              max={40}
              step={1}
              placeholder="0 - 40"
            />
          </Field>

          <Field label="Loan Purpose" required error={errors.purchaseType}>
            <div className="flex gap-4 whitespace-nowrap text-sm">
              {["Purchase", "Remortgage", "Product Transfer"].map((opt) => {
                const active = hasMulti(form.purchaseType, opt);
                return (
                  <label
                    key={opt}
                    className="inline-flex items-center gap-2 text-slate-800"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      checked={active}
                      onChange={() =>
                        setField("purchaseType", toggleMulti(form.purchaseType, opt))
                      }
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          </Field>

          <Field label="Product Term (Years)" required error={errors.productTerm}>
            <select
              data-key="productTerm"
              className={`w-full border rounded p-2 bg-white ${
                errors.productTerm ? "border-rose-500" : ""
              }`}
              value={form.productTerm}
              onChange={(e) => setField("productTerm", e.target.value)}
            >
              <option value="">Select</option>
              <option>0.5</option>
              <option>1</option>
              <option>2</option>
              <option>3</option>
              <option>5</option>
              <option>10</option>
              <option>Lifetime</option>
            </select>
          </Field>

          {/* Row 3 */}
          <Field label="Borrower Type" required error={errors.borrowerType}>
            <div className="flex gap-4 whitespace-nowrap text-sm">
              {["Individual/partnership", "Ltd Co.", "Pension/Trust"].map((opt) => {
                const active = hasMulti(form.borrowerType, opt);
                return (
                  <label
                    key={opt}
                    className="inline-flex items-center gap-2 text-slate-800"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      checked={active}
                      onChange={() =>
                        setField("borrowerType", toggleMulti(form.borrowerType, opt))
                      }
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          </Field>

          <Field
            label="EPC rating — Is it a green product?"
            required
            error={errors.epcGreen}
          >
            <select
              data-key="epcGreen"
              className={`w-full border rounded p-2 bg-white ${
                errors.epcGreen ? "border-rose-500" : ""
              }`}
              value={form.epcGreen}
              onChange={(e) => setField("epcGreen", e.target.value)}
            >
              <option value="">Select</option>
              <option>Yes</option>
              <option>No</option>
            </select>
          </Field>
        </Section>

        {/* ---------- Mortgage Details ---------- */}
        <Section title="Mortgage Details">
          {[
            ["LTV (%)", "ltv", "e.g., 75"],
            ["Min Loan (£)", "minLoan", "0.00"],
            ["Max Loan (£)", "maxLoan", "0.00"],
            ["Product Rate (%)", "rate", "e.g., 4.49"],
            ["APRC (%)", "aprc", "e.g., 6.1"],
            ["SVR Margin (%)", "svr", "e.g., 3.50"],
          ].map(([lab, key, ph]) => (
            <Field key={key} label={lab} required error={errors[key]}>
              <input
                data-key={key}
                type="number"
                className={`w-full border rounded p-2 bg-white ${
                  errors[key] ? "border-rose-500" : ""
                }`}
                value={form[key]}
                onChange={(e) => setField(key, e.target.value)}
                min={0}
                {...(key === "ltv" ? { max: 100 } : {})}
                step="0.01"
                placeholder={ph}
              />
            </Field>
          ))}
        </Section>

        {/* ---------- Debt Service Coverage ---------- */}
        <Section title="Debt Service Coverage">
          {[
            ["Basic Tax Payer DSC (%)", "dscBasic"],
            ["High Tax Payer DSC (%)", "dscHigh"],
            ["ICR Rate (%)", "icr"],
          ].map(([lab, key]) => (
            <Field key={key} label={lab} required error={errors[key]}>
              <input
                data-key={key}
                type="number"
                className={`w-full border rounded p-2 bg-white ${
                  errors[key] ? "border-rose-500" : ""
                }`}
                value={form[key]}
                onChange={(e) => setField(key, e.target.value)}
                min={key === "icr" ? 0.01 : 100}
                step="0.01"
                placeholder={key === "icr" ? "4.39%" : "≥ 100"}
              />
            </Field>
          ))}
        </Section>

        {/* ---------- Fee Structure (aligned inputs) ---------- */}
        <Section title="Fee Structure">
          <Field label="Product Fee" required error={errors.prodFee}>
            {/* Row: input (left) + mode toggle (right) */}
            <div className="flex items-center gap-3">
              {/* Input with dynamic prefix */}
              <div className="relative flex-1">
                {form.productFeeMode === "pound" && (
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                    £
                  </span>
                )}
                {form.productFeeMode === "percent" && (
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                    %
                  </span>
                )}
                {form.productFeeMode === "min" && (
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-500">
                    Min £
                  </span>
                )}

                <input
                  data-key="prodFee"
                  type="number"
                  className={`w-full border rounded p-2 bg-white text-sm ${
                    errors.prodFee ? "border-rose-500" : ""
                  } ${
                    form.productFeeMode === "percent"
                      ? "pl-10"
                      : form.productFeeMode === "min"
                      ? "pl-14"
                      : "pl-8"
                  }`}
                  value={form.prodFee}
                  onChange={(e) => setField("prodFee", e.target.value)}
                  min={0}
                  step="0.01"
                  placeholder={
                    form.productFeeMode === "pound"
                      ? "e.g., 999"
                      : form.productFeeMode === "percent"
                      ? "e.g., 1.50"
                      : "e.g., 1495"
                  }
                />
              </div>

              {/* Compact mode toggle to the right */}
              <div
                className="inline-flex rounded-full bg-slate-100 p-1 text-[11px] font-medium shrink-0"
                role="radiogroup"
              >
                {[
                  { id: "pound", label: "£ Flat" },
                  { id: "percent", label: "% Loan" },
                  { id: "min", label: "Min £" },
                ].map((opt) => {
                  const active = form.productFeeMode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setField("productFeeMode", opt.id)}
                      className={`px-2.5 py-0.5 rounded-full transition-colors ${
                        active
                          ? "bg-white text-sky-700 shadow-sm"
                          : "text-slate-600 hover:text-slate-800"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <p className="mt-1 text-[11px] text-slate-500">
              {form.productFeeMode === "pound"
                ? "Flat fee in pounds."
                : form.productFeeMode === "percent"
                ? "Percentage of the loan amount."
                : "Minimum arrangement fee in pounds."}
            </p>
          </Field>

          <Field label="Application Fee (£)" required error={errors.appFee}>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                £
              </span>
              <input
                data-key="appFee"
                type="number"
                className={`w-full border rounded p-2 bg-white pl-8 ${
                  errors.appFee ? "border-rose-500" : ""
                }`}
                value={form.appFee}
                onChange={(e) => setField("appFee", e.target.value)}
                min={0}
                step="0.01"
                placeholder="0.00"
              />
            </div>
          </Field>
        </Section>

        {/* ---------- Early Repayment Charges ---------- */}
        <Section title="Early Repayment Charges">
          {["erc1", "erc2", "erc3", "erc4", "erc5"].map((k, i) => (
            <Field key={k} label={`Year ${i + 1} (%)`} required error={errors[k]}>
              <input
                data-key={k}
                type="number"
                className={`w-full border rounded p-2 bg-white ${
                  errors[k] ? "border-rose-500" : ""
                }`}
                value={form[k]}
                onChange={(e) => setField(k, e.target.value)}
                min={0}
                step="0.01"
                placeholder={`e.g., ${5 - i}`}
              />
            </Field>
          ))}
        </Section>

        {/* ---------- Property Requirements ---------- */}
        <Section title="Property Requirements">
          <Field label="Home Owner" required error={errors.homeOwner}>
            <select
              data-key="homeOwner"
              className={`w-full border rounded p-2 bg-white ${
                errors.homeOwner ? "border-rose-500" : ""
              }`}
              value={form.homeOwner}
              onChange={(e) => setField("homeOwner", e.target.value)}
            >
              <option value="">Select</option>
              {["Yes", "No"].map((o, i) => (
                <option key={i} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </Field>

          <Field label="BTL Experience (years)" required error={errors.btlExp}>
            <div className="flex gap-4 whitespace-nowrap text-sm">
              {["0", "1-5", "5+"].map((opt) => {
                const active = hasMulti(form.btlExp, opt);
                return (
                  <label
                    key={opt}
                    className="inline-flex items-center gap-2 text-slate-800"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      checked={active}
                      onChange={() =>
                        setField("btlExp", toggleMulti(form.btlExp, opt))
                      }
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          </Field>

          <Field
            label="Credit Worthiness"
            required
            error={errors.creditWorth}
          >
            <div className="flex gap-4 whitespace-nowrap text-sm">
              {["Excellent 881+", "Fair 721-880", "Poor-LT550"].map((opt) => {
                const active = hasMulti(form.creditWorth, opt);
                return (
                  <label
                    key={opt}
                    className="inline-flex items-center gap-2 text-slate-800"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      checked={active}
                      onChange={() =>
                        setField("creditWorth", toggleMulti(form.creditWorth, opt))
                      }
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          </Field>

          <Field label="Tenancy Type" required error={errors.tenancyType}>
            <div className="flex gap-4 whitespace-nowrap text-sm">
              {/* "License" must match backend exactly */}
              {["AST", "Lease", "License"].map((opt) => {
                const active = hasMulti(form.tenancyType, opt);
                return (
                  <label
                    key={opt}
                    className="inline-flex items-center gap-2 text-slate-800"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      checked={active}
                      onChange={() =>
                        setField("tenancyType", toggleMulti(form.tenancyType, opt))
                      }
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          </Field>

          {/* F/H or L/H BEFORE lease term; lease term only when LEASEHOLD */}
          <Field label="F/H or L/H" required error={errors.tenure}>
            <div className="flex gap-4 whitespace-nowrap text-sm">
              {["FREEHOLD", "LEASEHOLD"].map((opt) => {
                const active = hasMulti(form.tenure, opt);
                return (
                  <label
                    key={opt}
                    className="inline-flex items-center gap-2 text-slate-800"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      checked={active}
                      onChange={() =>
                        setField("tenure", toggleMulti(form.tenure, opt))
                      }
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          </Field>

          {isLeasehold && (
            <Field
              label="Leasehold Remaining Term (Years)"
              required
              error={errors.leaseYears}
            >
              <input
                data-key="leaseYears"
                type="number"
                className={`w-full border rounded p-2 bg-white ${
                  errors.leaseYears ? "border-rose-500" : ""
                }`}
                value={form.leaseYears}
                onChange={(e) => setField("leaseYears", e.target.value)}
                min={0}
                step="1"
                placeholder="e.g., 85"
              />
            </Field>
          )}

          <Field label="Ex Local Authority" required error={errors.exLocal}>
            <select
              data-key="exLocal"
              className={`w-full border rounded p-2 bg-white ${
                errors.exLocal ? "border-rose-500" : ""
              }`}
              value={form.exLocal}
              onChange={(e) =>
                setField("exLocal", normalizeYesNo(e.target.value))
              }
            >
              {["", "No", "Yes"].map((o, i) => (
                <option key={i} value={o}>
                  {o || "Select"}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Deposit Origination"
            required
            error={errors.depositOrig}
          >
            <div className="flex gap-4 whitespace-nowrap text-sm">
              {["Savings", "Gifted", "Equity"].map((opt) => {
                const active = hasMulti(form.depositOrig, opt);
                return (
                  <label
                    key={opt}
                    className="inline-flex items-center gap-2 text-slate-800"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      checked={active}
                      onChange={() =>
                        setField("depositOrig", toggleMulti(form.depositOrig, opt))
                      }
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          </Field>

          <Field label="Day 1 Remortgage" required error={errors.day1Remo}>
            <div className="flex gap-4 whitespace-nowrap text-sm">
              {["Pound for Pound", "Market Value", "Refurb"].map((opt) => {
                const active = hasMulti(form.day1Remo, opt);
                return (
                  <label
                    key={opt}
                    className="inline-flex items-center gap-2 text-slate-800"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      checked={active}
                      onChange={() =>
                        setField("day1Remo", toggleMulti(form.day1Remo, opt))
                      }
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          </Field>
        </Section>

        {/* ---------- Product Features ---------- */}
        <div className="rounded-2xl border border-sky-200 bg-sky-50/40 p-4">
          <div className="text-sky-900 font-semibold mb-3">
            Product Features
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.keys(form.features).map((k) => (
              <label
                key={k}
                className="inline-flex items-center gap-2 text-sky-900"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.features[k]}
                  onChange={(e) => setFeature(k, e.target.checked)}
                />
                {k}
              </label>
            ))}

            {/* Cashback amount */}
            <label className="inline-flex items-center gap-2 text-sky-900">
              <span>Cashback (£)</span>
              <input
                data-key="cashback"
                type="number"
                min="0"
                step="0.01"
                className={`w-24 border rounded p-1 text-gray-700 ${
                  errors?.cashback ? "border-rose-500" : ""
                }`}
                placeholder="0"
                value={form.cashback}
                onChange={(e) => setField("cashback", e.target.value)}
              />
            </label>
          </div>

          {errors?.cashback ? (
            <p className="mt-1 text-xs text-rose-600">{errors.cashback}</p>
          ) : null}
        </div>

        {/* ---------- Complex BTL (collapsible) ---------- */}
        <ComplexBtlPanel
          open={complexOpen}
          onToggle={() => setComplexOpen((o) => !o)}
          values={form.complex}
          onChange={setComplexField}
          errors={errors}
        />

        {/* ——— Valuation & Legal Documents ——— */}
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/50 p-6 shadow-sm">
          <div className="mb-5">
            <div className="flex items-center gap-2 text-slate-900 font-semibold text-lg">
              <svg
                className="w-5 h-5 text-sky-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Valuation & Legal Documents
            </div>
            <p className="text-sm text-slate-500 mt-1.5">
              Upload spreadsheets in .xlsx, .xls, .csv, or .ods format (max{" "}
              {MAX_MB}
              MB per file)
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Valuation Upload */}
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-sm">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Valuation Fee Schedule
                  </div>
                  <div className="text-xs text-slate-500">
                    Required unless free valuation selected
                  </div>
                </div>
              </div>

              <div
                className={
                  "group relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all duration-200 p-8 cursor-pointer " +
                  (freeValuationOn
                    ? "border-slate-200 bg-slate-50/50 opacity-50 cursor-not-allowed"
                    : hasValuation
                    ? "border-sky-300 bg-sky-50/50 hover:bg-sky-100/50 hover:border-sky-400"
                    : "border-slate-300 bg-white hover:bg-slate-50 hover:border-sky-400")
                }
                title={
                  freeValuationOn
                    ? "Disabled because 'Free Valuation' is selected"
                    : "Click to upload or change file"
                }
                onClick={() => {
                  if (freeValuationOn) return;
                  if (valuationInputRef.current) {
                    // allow selecting same file again
                    valuationInputRef.current.value = "";
                    valuationInputRef.current.click();
                  }
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (freeValuationOn) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (valuationInputRef.current) {
                      valuationInputRef.current.value = "";
                      valuationInputRef.current.click();
                    }
                  }
                }}
              >
                <input
                  ref={valuationInputRef}
                  data-key="valuationDoc"
                  type="file"
                  disabled={freeValuationOn}
                  accept=".xlsx,.xls,.csv,.ods,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.oasis.opendocument.spreadsheet,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    if (f) {
                      handleImmediateFileUpload(f, "valuationDoc");
                    }
                  }}
                />

                {/* Upload Progress Bar */}
                {uploadingFiles.valuationDoc && (
                  <div className="w-full mb-3 rounded-lg border border-sky-300 bg-sky-50 p-3 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <svg
                          className="h-4 w-4 text-sky-600 animate-spin flex-shrink-0"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span className="text-xs font-medium text-slate-700 truncate" title={uploadingFiles.valuationDoc.name}>
                          {uploadingFiles.valuationDoc.name}
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-sky-600 ml-2 flex-shrink-0">
                        {uploadingFiles.valuationDoc.progress || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-sky-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-sky-600 h-full rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${uploadingFiles.valuationDoc.progress || 0}%` }}
                      />
                    </div>
                  </div>
                )}

                {!hasValuation && !uploadingFiles.valuationDoc ? (
                  <>
                    <div className="relative mb-3">
                      <div className="absolute inset-0 bg-sky-500/20 blur-xl rounded-full" />
                      <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 shadow-lg group-hover:shadow-xl transition-shadow">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-8 w-8 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-semibold text-slate-900 mb-1">
                        Drop your file here
                      </div>
                      <div className="text-xs text-slate-600 mb-2">
                        or click to browse
                      </div>
                      <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Spreadsheet files only
                      </div>
                    </div>
                  </>
                ) : !uploadingFiles.valuationDoc ? (
                  <FileChip
                    file={form.valuationDoc}
                    onClear={() => {
                      setField("valuationDoc", null);
                      setUploadedFileUrls((prev) => {
                        const next = { ...prev };
                        delete next.valuationDoc;
                        return next;
                      });
                      setErrors((prev) => ({
                        ...prev,
                        valuationDoc: errRequired,
                      }));
                      if (valuationInputRef.current) {
                        valuationInputRef.current.value = "";
                      }
                    }}
                  />
                ) : null}
              </div>
              {errors.valuationDoc ? (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-rose-600">
                  <svg
                    className="w-4 h-4 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {errors.valuationDoc}
                </div>
              ) : null}
            </div>

            {/* Legal Fees Upload */}
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
                    />
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Legal Fees Schedule
                  </div>
                  <div className="text-xs text-slate-500">
                    Required for all products
                  </div>
                </div>
              </div>

              <div
                className={
                  "group relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all duration-200 p-8 cursor-pointer " +
                  (hasLegal
                    ? "border-emerald-300 bg-emerald-50/50 hover:bg-emerald-100/50 hover:border-emerald-400"
                    : "border-slate-300 bg-white hover:bg-slate-50 hover:border-emerald-400")
                }
                title="Click to upload or change file"
                onClick={() => {
                  if (legalInputRef.current) {
                    legalInputRef.current.value = "";
                    legalInputRef.current.click();
                  }
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (legalInputRef.current) {
                      legalInputRef.current.value = "";
                      legalInputRef.current.click();
                    }
                  }
                }}
              >
                <input
                  ref={legalInputRef}
                  data-key="legalFeesDoc"
                  type="file"
                  accept=".xlsx,.xls,.csv,.ods,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.oasis.opendocument.spreadsheet,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    if (f) {
                      handleImmediateFileUpload(f, "legalFeesDoc");
                    }
                  }}
                />

                {/* Upload Progress Bar */}
                {uploadingFiles.legalFeesDoc && (
                  <div className="w-full mb-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <svg
                          className="h-4 w-4 text-emerald-600 animate-spin flex-shrink-0"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span className="text-xs font-medium text-slate-700 truncate" title={uploadingFiles.legalFeesDoc.name}>
                          {uploadingFiles.legalFeesDoc.name}
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-emerald-600 ml-2 flex-shrink-0">
                        {uploadingFiles.legalFeesDoc.progress || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-emerald-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-emerald-600 h-full rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${uploadingFiles.legalFeesDoc.progress || 0}%` }}
                      />
                    </div>
                  </div>
                )}

                {!hasLegal && !uploadingFiles.legalFeesDoc ? (
                  <>
                    <div className="relative mb-3">
                      <div className="absolute inset-0 bg-emerald-500/20 blur-xl rounded-full" />
                      <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg group-hover:shadow-xl transition-shadow">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-8 w-8 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-semibold text-slate-900 mb-1">
                        Drop your file here
                      </div>
                      <div className="text-xs text-slate-600 mb-2">
                        or click to browse
                      </div>
                      <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Spreadsheet files only
                      </div>
                    </div>
                  </>
                ) : (
                  <FileChip
                    file={form.legalFeesDoc}
                    onClear={() => {
                      setField("legalFeesDoc", null);
                      setUploadedFileUrls((prev) => {
                        const next = { ...prev };
                        delete next.legalFeesDoc;
                        return next;
                      });
                      setErrors((prev) => ({
                        ...prev,
                        legalFeesDoc: errRequired,
                      }));
                      if (legalInputRef.current) {
                        legalInputRef.current.value = "";
                      }
                    }}
                  />
                )}
              </div>
              {errors.legalFeesDoc ? (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-rose-600">
                  <svg
                    className="w-4 h-4 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {errors.legalFeesDoc}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* ---------- Scheduling ---------- */}
        <div className="rounded-2xl border border-sky-200 bg-sky-50/40 p-4">
          <div className="flex items-center justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-sky-900 font-semibold">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={form.scheduleEnabled}
                onChange={(e) => setField("scheduleEnabled", e.target.checked)}
              />
              Schedule this product to go live later
            </label>
            <div className="min-w-[260px]">
              <input
                data-key="scheduleAt"
                type="datetime-local"
                className={`w-full border rounded p-2 bg-white ${
                  errors.scheduleAt ? "border-rose-500" : ""
                }`}
                disabled={!form.scheduleEnabled}
                value={form.scheduleAt}
                onChange={(e) => setField("scheduleAt", e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-2">
            If enabled, the product will be queued to publish automatically at
            the selected date & time.
          </p>
          {errors.scheduleAt && (
            <p className="text-rose-600 text-sm mt-1">{errors.scheduleAt}</p>
          )}
        </div>

        {/* ---------- Submit + Status ---------- */}
        <div className="mt-8 mb-2 flex items-center justify-end gap-4">
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 rounded-lg bg-sky-600 text-white font-semibold disabled:opacity-60 flex items-center gap-2"
          >
            {submitting && (
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            )}
            {submitting
              ? "Submitting…"
              : form.scheduleEnabled
              ? "Schedule Product"
              : "Submit Product"}
          </button>
        </div>
      </form>

      {/* Confirmation Modal */}
      {uploadResult && (
        <ConfirmationModal
          open={showConfirmation}
          onClose={handleConfirmationClose}
          title={
            uploadResult.type === "error"
              ? "Product Upload Failed"
              : uploadResult.isScheduled
              ? "Product Scheduled Successfully"
              : "Product Uploaded Successfully"
          }
          message={
            uploadResult.type === "error"
              ? uploadResult.message
              : uploadResult.isScheduled
              ? `Your product "${uploadResult.productName || "Product"}" has been scheduled for publication.`
              : `Your product "${uploadResult.productName || "Product"}" has been uploaded and is now live in the system.`
          }
          type={uploadResult.type === "error" ? "error" : "success"}
          details={
            uploadResult.type === "error"
              ? null
              : [
                  `Product Name: ${uploadResult.productName || "N/A"}`,
                  `Product ID: ${uploadResult.productId || "N/A"}`,
                  `Product Type: ${uploadResult.productType || "N/A"}`,
                  uploadResult.isScheduled
                    ? `Scheduled for: ${uploadResult.scheduledAt || "N/A"}`
                    : "Status: Live",
                ]
          }
          confirmLabel="OK"
          showCancel={false}
        />
      )}
    </>
  );
}

export default function ManualEntryPage() {
  return <ManualEntryBtlForm />;
}